import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Table,
  InputNumber,
  Popconfirm,
  Button,
  Form,
  Space,
  Row,
  Col,
  Statistic,
  Select,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import Utilities from "../../../dependencies/custom/react-utilities";
import Settings from "../../../dependencies/custom/settings";
import { ArrowLeftOutlined } from "@ant-design/icons";
import useTable from "../../../hooks/table";
import useUpload from "../../../hooks/upload";
import useExcel from "../../../hooks/excel";
import CustomFunctions from "../../../dependencies/custom/custom-functions";
import useModal from "../../../hooks/modal";

const EXAM_PERIOD_OPTIONS = [
  { value: "BOT", label: "BOT" },
  { value: "MID", label: "MID" },
  { value: "EOT", label: "EOT" },
];

const PERIOD_ENTRY_FIELDS = {
  BOT: ["bot"],
  MID: ["mid"],
  EOT: ["internalExam", "externalExam"],
};

const PrimaryAssessment = () => {
  const uploadRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const details = location.state;
  const { row, readOnly } = details || {};
  const [subjectAssignment, setSubjectAssignment] = useState(row || {});

  const [form] = Form.useForm();
  const [showConfirm, setShowConfirm] = useState(false);
  const [statusOpt, setStatusOpt] = useState();
  const [disabled, setDisabled] = useState(readOnly || parseInt(row?.locked));
  const [periodType, setPeriodType] = useState("EOT");
  const periodTypeRef = useRef("EOT");
  const excel = useExcel();
  const upload = useUpload();
  const modal = useModal();
  const scoreFields = ["bot", "mid", "internalExam", "externalExam"];
  const entryFields = PERIOD_ENTRY_FIELDS[periodType] || [];
  const templateHeading = ["studentID", "studentName", ...scoreFields];

  useEffect(() => {
    periodTypeRef.current = periodType;
  }, [periodType]);

  function refreshSubjectAssignment() {
    return CustomFunctions.getSubjectAssignment(
      setDisabled,
      navigate,
      readOnly,
      subjectAssignment,
      false,
      setSubjectAssignment,
    );
  }

  const table = useTable(
    {
      pagination: {
        current: 1,
        pageSize: 20,
        hideOnSinglePage: true,
      },
    },
    undefined,
    undefined,
    undefined,
    undefined,
    "id",
    {},
    {},
  );

  function setMarks(data, groupKey = "studentID") {
    const grouped = Utilities.groupBy(data, groupKey);
    const current = form.getFieldsValue() || {};
    const merged = { ...current };
    Object.entries(grouped).forEach(([studentID, marks]) => {
      const m = marks[0] || {};
      const incoming = {
        studentID,
        bot: m.bot ?? undefined,
        mid: m.mid ?? undefined,
        internalExam: m.int_exam ?? m.internalExam ?? undefined,
        externalExam: m.ext_exam ?? m.externalExam ?? undefined,
        remarks: m.remarks ?? undefined,
      };
      const cleaned = Object.fromEntries(
        Object.entries(incoming).filter(
          ([, value]) => value !== undefined && value !== null && value !== "",
        ),
      );
      merged[studentID] = { ...(current[studentID] || {}), ...cleaned };
    });

    form.setFieldsValue(merged);
  }

  const cellValue = (row, field) => {
    if (field === "internalExam") return row.int_exam ?? row.internalExam;
    if (field === "externalExam") return row.ext_exam ?? row.externalExam;
    return row[field];
  };

  const hasValue = (value) =>
    value !== undefined && value !== null && value !== "";

  function applyUploadedMarks(data, groupKey = "studentID") {
    const rows = Array.isArray(data) ? data : [];
    const activePeriod = periodTypeRef.current;
    const activeFields = PERIOD_ENTRY_FIELDS[activePeriod] || [];
    const captured = activeFields.filter((field) =>
      rows.some((row) => hasValue(cellValue(row, field))),
    );
    if (!captured.length) {
      Utilities.showNotification(
        undefined,
        `No ${activePeriod} marks were found in the uploaded file`,
      );
      return false;
    }
    const current = form.getFieldsValue() || {};
    const merged = { ...current };
    const grouped = Utilities.groupBy(rows, groupKey);
    Object.entries(grouped).forEach(([studentID, marks]) => {
      const m = marks[0] || {};
      const next = { ...(current[studentID] || {}), studentID };
      captured.forEach((field) => {
        const value = cellValue(m, field);
        next[field] = hasValue(value) ? value : null;
      });
      merged[studentID] = next;
    });
    form.setFieldsValue(merged);
    return true;
  }

  async function getMarks() {
    const res = await Utilities.fetchWithAuth(
      "GET",
      `${Settings?.urls?.academics?.scoresheet}/${subjectAssignment?.id}`,
      undefined,
      navigate,
    );
    if (res?.ok) {
      setMarks(res?.data);
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
  }

  const columns = [
    {
      title: "Student ID",
      dataIndex: "id",
      key: "id",
      render: (v, record) => <label>{v}</label>,
    },
    {
      title: "Student",
      // dataIndex: "name",
      key: "name",
      render: (v, record) => (
        <Form.Item
          name={[record.id, "studentID"]}
          noStyle
          initialValue={record.id}
        >
          <label>{CustomFunctions.formatStudentName(record)}</label>
        </Form.Item>
      ),
    },
    {
      title: "BoT",
      key: "bot",
      render: (_, record) => (
        <Form.Item name={[record.id, "bot"]} noStyle>
          <InputNumber
            min={0}
            disabled={disabled || !entryFields.includes("bot")} /*max={30}*/
          />
        </Form.Item>
      ),
    },
    {
      title: "Mid-Term",
      key: "mid",
      render: (_, record) => (
        <Form.Item name={[record.id, "mid"]} noStyle>
          <InputNumber
            min={0}
            disabled={disabled || !entryFields.includes("mid")} /*max={20}*/
          />
        </Form.Item>
      ),
    },
    {
      title: "Internal Exam",
      key: "internalExam",
      render: (_, record) => (
        <Form.Item name={[record.id, "internalExam"]} noStyle>
          <InputNumber
            min={0}
            disabled={
              disabled || !entryFields.includes("internalExam")
            } /*max={50}*/
          />
        </Form.Item>
      ),
    },
    {
      title: "External Exam",
      key: "externalExam",
      render: (_, record) => (
        <Form.Item name={[record.id, "externalExam"]} noStyle>
          <InputNumber
            min={0}
            disabled={
              disabled || !entryFields.includes("externalExam")
            } /*max={50}*/
          />
        </Form.Item>
      ),
    },
  ];

  useEffect(() => {
    table.setColumns(columns);
    CustomFunctions.getStudents(table, subjectAssignment, navigate, getMarks);
    onUpload();
  }, []);

  useEffect(() => {
    table.setColumns(columns);
  }, [periodType, disabled]);

  useEffect(() => {
    refreshSubjectAssignment();
  }, []);

  // async function getSubjectAssignment(donotMakeRequest = true) {
  //   if (readOnly && donotMakeRequest) return;
  //   const res = await Utilities.fetchWithAuth('GET', `${Settings?.urls?.academics?.subject_assignments}/${row?.id}`, undefined, navigate);
  //   if (res?.ok) {
  //     setDisabled(parseInt(res?.data?.locked));
  //   } else {
  //     if (res?.msg) Utilities.showNotification(undefined, res?.msg);
  //   }
  // }

  function onUploadClicked() {
    if (disabled) {
      Utilities.showNotification(undefined, "Action is forbidden");
      return;
    }
    const element = upload.uploaderHTMLElement(uploadRef.current);
    element?.click();
  }

  function onUpload() {
    upload.setCustomBeforeUpload({
      async beforeUpload(file, acceptedFiles) {
        if (disabled) {
          Utilities.showNotification(
            undefined,
            "Action is forbidden when scores have been submitted",
          );
          return;
        }
        try {
          // Show the loader so the user understands the upload is being processed.
          table.setLoading(true);
          // Yield so the loader paints before parsing the (potentially large) file.
          await Utilities.sleep(50);
          const result = await excel.importXLSX(file);
          if (applyUploadedMarks(result)) {
            Utilities.showNotification(
              undefined,
              "Marks have been set successfully",
              "text-success",
            );
          }
        } catch (e) {
          Utilities.showNotification(
            undefined,
            "Failed to read the uploaded file",
          );
        } finally {
          table.setLoading(false);
        }
      },
    });
  }

  const onFinish = async (isSubmit, isAutoSave = true) => {
    if (disabled) {
      Utilities.showNotification(undefined, "Action is forbidden");
      setShowConfirm(false);
      return;
    }
    const values = form.getFieldsValue();
    if (!Object.keys(values)?.length) {
      Utilities.showNotification(undefined, "There is no data to save");
      return;
    }
    const students = {};
    Object.entries(values).forEach(([studentID, studentValues = {}]) => {
      const marks = {};
      entryFields.forEach((field) => {
        const value = studentValues?.[field];
        if (value === undefined) return;
        marks[field] = value === "" ? null : value;
      });
      if (Object.keys(marks).length) students[studentID] = marks;
    });
    if (!Object.keys(students).length) {
      Utilities.showNotification(
        undefined,
        `There are no ${periodType} marks to save`,
      );
      return;
    }
    const res = await Utilities.fetchWithAuth(
      "POST",
      Settings?.urls?.academics?.scoresheet,
      { subjectTeacherID: subjectAssignment?.id, periodType, students },
      navigate,
    );
    // console.log(res)
    if (res?.ok) {
      if (isSubmit) {
        const res1 = await Utilities.fetchWithAuth(
          "POST",
          Settings?.urls?.academics?.submit,
          { subjectTeacherID: subjectAssignment?.id },
          navigate,
        );
        if (res1?.ok) {
          Utilities.showNotification(
            undefined,
            "Scores have been submitted successfully",
            "text-success",
          );
        } else {
          Utilities.showNotification(undefined, res1?.msg);
        }
        setShowConfirm(false);
        refreshSubjectAssignment();
      } else {
        Utilities.showNotification(
          undefined,
          "Scores have been saved successfully",
          "text-success",
        );
      }
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
  };

  async function changeStatus() {
    modal.setLoading(true);
    const res = await Utilities.fetchWithAuth(
      "POST",
      Settings?.urls?.academics?.update_status,
      { subjectTeacherID: subjectAssignment?.id, status: statusOpt },
      navigate,
    );
    if (res?.ok) {
      modal.setOpen(false);
      setStatusOpt(undefined);
      await refreshSubjectAssignment();
      Utilities.showNotification(
        undefined,
        "Status has been updated successfully",
        "text-success",
      );
    } else {
      Utilities.showNotification(undefined, res?.msg);
    }
    modal.setLoading(false);
  }

  return (
    <Row gutter={[24, 24]}>
      <Col xs={24} md={12} lg={8}>
        <Card variant="borderless" className="shadow">
          <Statistic
            title="Class"
            value={subjectAssignment?.className}
            suffix={subjectAssignment?.streamName}
          />
        </Card>
      </Col>

      <Col xs={24} md={12} lg={4}>
        <Card variant="borderless" className="shadow">
          <Statistic
            title="Academic Year"
            value={subjectAssignment?.academicYear}
            formatter={(val) => val}
          />
        </Card>
      </Col>
      <Col xs={24} md={12} lg={4}>
        <Card variant="borderless" className="shadow">
          <Statistic title="Term" value={subjectAssignment?.term} />
        </Card>
      </Col>

      <Col xs={24} md={12} lg={8}>
        <Card variant="borderless" className="shadow">
          <Statistic title="Subject" value={subjectAssignment?.subjectName} />
        </Card>
      </Col>

      <Col xs={24} md={12} lg={8}>
        <Card variant="borderless" className="shadow">
          <Statistic title="Teacher" value={subjectAssignment?.teacherName} />
        </Card>
      </Col>
      <Col xs={24} md={12} lg={8}>
        <Card variant="borderless" className="shadow">
          <Statistic title="Status" value={subjectAssignment?.status} />
        </Card>
      </Col>

      <Col xs={24}>
        <Card
          title={
            <>
              {" "}
              <Button
                className="me-1"
                onClick={(e) => navigate(-1)}
                icon={<ArrowLeftOutlined />}
              >
                Back
              </Button>{" "}
              Assessment Entry
            </>
          }
          bordered={false}
          extra={
            <Space wrap className="my-3">
              {upload.uploader(
                "FileUpload",
                "",
                "upload",
                [
                  "text/csv",
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  "application/vnd.ms-excel",
                ],
                "text",
                "d-none",
                uploadRef,
              )}
              <Select
                value={periodType}
                onChange={setPeriodType}
                style={{ width: 150 }}
                disabled={disabled}
                options={EXAM_PERIOD_OPTIONS}
              />
              <Button
                className="me-2x mb-2x"
                icon={<i className="fas fa-download" />}
                type="primary"
                onClick={(e) =>
                  CustomFunctions.onDownloadTemplate(
                    table,
                    excel,
                    templateHeading,
                  )
                }
              >
                Download Template
              </Button>
              <Button
                className="me-2x"
                icon={<i className="fas fa-download" />}
                type="primary"
                onClick={(e) =>
                  CustomFunctions.onDownloadSheet(
                    table,
                    form,
                    excel,
                    templateHeading,
                  )
                }
              >
                Download Scores
              </Button>
              {!readOnly && (
                <Button
                  icon={<i className="fas fa-upload" />}
                  className="btn-success border-0 me-2x"
                  onClick={(e) => onUploadClicked()}
                >
                  Upload Scores
                </Button>
              )}
              {!readOnly && (
                <Button
                  onClick={(e) => onFinish(false)}
                  icon={<i className="fas fa-save" />}
                  className="btn-success border-0 me-2x"
                >
                  Save{" "}
                </Button>
              )}
              {!readOnly && (
                <Popconfirm
                  title="Submit Scores"
                  description="Confirm scores submission? This action will lock the assessment sheet."
                  onConfirm={(e) => onFinish(true)}
                  onCancel={(e) => setShowConfirm(false)}
                  placement="left"
                  okText="Yes"
                  cancelText="No"
                  open={showConfirm}
                >
                  <Button
                    icon={<i className="fas fa-paper-plane" />}
                    className="btn-success border-0"
                    onClick={(e) => setShowConfirm(true)}
                  >
                    Submit{" "}
                  </Button>
                </Popconfirm>
              )}
              {!!disabled && (
                <Button
                  onClick={(e) => modal.setOpen(true)}
                  icon={<i className="fas fa-sync" />}
                  className="btn-secondary border-0 me-2x"
                >
                  Change Status{" "}
                </Button>
              )}
            </Space>
          }
        >
          <Row gutter={[24, 24]}>
            <Col xs={24}>
              <Form form={form} layout="vertical" disabled={disabled}>
                <div className="table-responsivex">
                  {table.tableWithHeaderFooter(
                    CustomFunctions.tblCaption,
                    CustomFunctions.tblCaption,
                    { scroll: { x: "max-content" } },
                  )}
                </div>
                {/* <Space>
          <Button icon={<i className="fas fa-save" />} className="btn-success border-0 mt-5">Save </Button>
          <Button icon={<i className="fas fa-paper-plane" />} className="btn-success border-0 mt-5">Submit </Button>
        </Space> */}
              </Form>
            </Col>
          </Row>
        </Card>
      </Col>
      {modal.modalJSX(
        changeStatus,
        <>
          <label>Status</label>
          <Select
            className="w-100"
            onChange={(v) => setStatusOpt(v)}
            value={statusOpt}
            placeholder="Please select status"
            options={[
              { value: "Released", label: "Release" },
              { value: "Submitted", label: "Submit" },
            ]}
          />
        </>,
        undefined,
        { title: "Change status" },
      )}
    </Row>
  );
};

export default PrimaryAssessment;
